'use client';

import type { CartShowGiftDto } from '@/api/types/gift';
import { useReporting } from '@/hooks/useReporting';
import useRouteInfo from '@/hooks/useRouteInfo';
import { ProviderType, type TrackingEvent } from '@/reporting';
import { useCallback, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

export interface GiftLayerTrackingProperties {
  campaign_type_id_list: string[];
  tier_id_list: string[];
  base_subtotal_amount: number;
  currency: string;
}

export type GiftLayerEventName = 'GiftLayer_Exp' | 'GiftLayer_Click' | 'GiftLayerPopUp_Exp';

export interface CartGiftLayerTracking {
  giftEntryRef: (node?: Element | null) => void;
  onGiftLayerClick: () => void;
}

export interface BuildCartGiftLayerEventOptions {
  eventName: GiftLayerEventName;
  showGift: CartShowGiftDto | undefined;
  baseSubtotal: string | number | undefined;
  currency: string;
  pageName: string;
  urlPath: string;
}

type GiftTrackingSource = 'preview' | 'dialog';

type GiftTrackingItem = {
  activity_id?: number;
  id?: string | number;
  activity_type_key?: string;
  tier_id?: number;
};

const getGiftActivityType = (activityTypeKey: string | undefined) => {
  if (activityTypeKey?.includes('full')) return '商品满赠';
  if (activityTypeKey?.includes('buy')) return '商品买赠';
  return undefined;
};

export function buildCartGiftLayerProperties(
  showGift: CartShowGiftDto | undefined,
  currency: string,
  baseSubtotal: string | number | undefined,
  maxPreviewGifts = 2,
  source: GiftTrackingSource = 'preview',
): GiftLayerTrackingProperties | null {
  const gifts: GiftTrackingItem[] =
    source === 'dialog' ? showGift?.detail_popup?.activities || [] : showGift?.preview_gifts || [];
  const trackedGifts = gifts.slice(0, maxPreviewGifts).flatMap((gift) => {
    const activityType = getGiftActivityType(gift.activity_type_key);
    const activityId = gift.activity_id ?? gift.id;
    if (activityId === undefined || gift.tier_id === undefined || !activityType) return [];

    return {
      campaignTypeId: `赠品活动_${activityType}@${activityId}@`,
      tireId: `赠品活动_${activityType}@${activityId}@_@${gift.tier_id}@`,
    };
  });
  const campaign_type_id_list = trackedGifts.map((gift) => gift.campaignTypeId);
  const tier_id_list = trackedGifts.map((gift) => gift.tireId);
  const base_subtotal_amount = Number(baseSubtotal);

  if (
    campaign_type_id_list.length === 0 ||
    tier_id_list.length === 0 ||
    !Number.isFinite(base_subtotal_amount) ||
    !currency
  ) {
    return null;
  }

  return {
    campaign_type_id_list,
    tier_id_list,
    base_subtotal_amount,
    currency,
  };
}

export function buildCartGiftLayerEvent({
  eventName,
  showGift,
  baseSubtotal,
  currency,
  pageName,
  urlPath,
}: BuildCartGiftLayerEventOptions): TrackingEvent | null {
  const isPopupExposure = eventName === 'GiftLayerPopUp_Exp';
  const properties = buildCartGiftLayerProperties(
    showGift,
    currency,
    baseSubtotal,
    isPopupExposure ? Number.POSITIVE_INFINITY : 2,
    isPopupExposure ? 'dialog' : 'preview',
  );

  if (!properties) return null;

  return {
    eventName,
    properties: {
      page_name: pageName,
      url_path: urlPath,
      ...properties,
    },
  };
}

export function createGiftLayerClickHandler(
  trackGiftLayerClick: () => void,
  openGiftDialog: () => void,
) {
  return () => {
    trackGiftLayerClick();
    openGiftDialog();
  };
}

export function shouldObserveGiftOfferSlide({
  index,
  activeIndex,
  rotating,
}: {
  index: number;
  activeIndex: number;
  rotating: boolean;
}) {
  return !rotating || index === activeIndex;
}

interface UseCartGiftTrackingOptions {
  showGift: CartShowGiftDto | undefined;
  baseSubtotal: string | number | undefined;
  currency: string;
  open: boolean;
}

export function useCartGiftTracking({
  showGift,
  baseSubtotal,
  currency,
  open,
}: UseCartGiftTrackingOptions): CartGiftLayerTracking {
  const { trackEvent } = useReporting();
  const { useCurrentPageName, useCurrentPathname } = useRouteInfo();
  const pageName = useCurrentPageName() || '其它';
  const urlPath =
    useCurrentPathname() || (typeof window === 'undefined' ? '' : window.location.pathname);
  const { ref: giftEntryRef, inView } = useInView({ triggerOnce: true });
  const lastExposureKeyRef = useRef<string | undefined>(undefined);
  const previousOpenRef = useRef(false);

  const track = useCallback(
    (eventName: GiftLayerEventName) => {
      const event = buildCartGiftLayerEvent({
        eventName,
        showGift,
        baseSubtotal,
        currency,
        pageName,
        urlPath,
      });
      if (event) trackEvent(event, [ProviderType.SENSORS]);
    },
    [baseSubtotal, currency, pageName, showGift, trackEvent, urlPath],
  );

  useEffect(() => {
    if (!inView) return;

    const properties = buildCartGiftLayerProperties(showGift, currency, baseSubtotal);
    const exposureKey = properties ? JSON.stringify(properties) : '';
    if (!exposureKey || lastExposureKeyRef.current === exposureKey) return;

    lastExposureKeyRef.current = exposureKey;
    track('GiftLayer_Exp');
  }, [baseSubtotal, currency, inView, showGift, track]);

  useEffect(() => {
    if (open && !previousOpenRef.current) track('GiftLayerPopUp_Exp');
    previousOpenRef.current = open;
  }, [open, track]);

  return {
    giftEntryRef,
    onGiftLayerClick: () => track('GiftLayer_Click'),
  };
}
